
import { Timestamp } from "firebase/firestore";
import { isValid, parseISO, format } from "date-fns";
import { DASHBOARD_METRICS_TIMEZONE } from "./timezoneEt";

/**
 * Ensures we have a valid date object
 * @param date Date input (can be Date, Timestamp, string, etc.)
 * @returns Valid Date object or current date as fallback
 */
export const ensureValidDate = (date: any): Date => {
  // Add more detailed logging if date might be problematic
  if (!date) {
    console.warn("[ensureValidDate] Null or undefined date provided, using current date");
    return new Date();
  }
  
  // If it's already a Date object and valid
  if (date instanceof Date && isValid(date)) {
    return date;
  }
  
  // If it's a Timestamp
  if (date && typeof date === 'object' && 'toDate' in date && typeof date.toDate === 'function') {
    try {
      const converted = date.toDate();
      if (isValid(converted)) {
        return converted;
      } else {
        console.warn("[ensureValidDate] Invalid date from Timestamp conversion", date);
      }
    } catch (e) {
      console.error("[ensureValidDate] Error converting Timestamp to Date", e);
    }
  }
  
  // If it's a string, try to parse it
  if (typeof date === 'string') {
    try {
      // Try ISO format first
      const parsed = parseISO(date);
      if (isValid(parsed)) {
        return parsed;
      }
      
      // If ISO parsing fails, try Unix timestamp
      const timestamp = Number(date);
      if (!isNaN(timestamp)) {
        const fromTimestamp = new Date(timestamp);
        if (isValid(fromTimestamp)) {
          return fromTimestamp;
        }
      }
    } catch (e) {
      console.error("[ensureValidDate] Error parsing date string:", date, e);
    }
  }
  
  // If it's a number (timestamp)
  if (typeof date === 'number' && !isNaN(date)) {
    const parsed = new Date(date);
    if (isValid(parsed)) {
      return parsed;
    }
  }
  
  // Fallback to current date
  console.warn("[ensureValidDate] Invalid date provided, using current date as fallback:", date);
  return new Date();
};

/**
 * Convert any date-like value to a Date object
 * @param dateInput Date input (can be Date, Timestamp, string, etc.)
 * @returns Date object or null if invalid
 */
export const toDate = (dateInput: any): Date | null => {
  if (!dateInput) return null;
  
  // If it's already a Date object
  if (dateInput instanceof Date) {
    return isValid(dateInput) ? dateInput : null;
  }
  
  // If it's a Timestamp
  if (dateInput instanceof Timestamp) {
    return dateInput.toDate();
  }
  
  // If it's a string, try to parse it
  if (typeof dateInput === 'string') {
    try {
      const parsed = parseISO(dateInput);
      return isValid(parsed) ? parsed : null;
    } catch (e) {
      console.error("Error parsing date string:", dateInput, e);
      return null;
    }
  }
  
  // If it's a number (timestamp)
  if (typeof dateInput === 'number') {
    const parsed = new Date(dateInput);
    return isValid(parsed) ? parsed : null;
  }
  
  // If it's an object with a toDate method (like Firebase Timestamp)
  if (dateInput && typeof dateInput === 'object' && 'toDate' in dateInput && typeof dateInput.toDate === 'function') {
    try {
      return dateInput.toDate();
    } catch (e) {
      console.error("Error converting object to date:", dateInput, e);
      return null;
    }
  }
  
  return null;
};

/**
 * Format a date with a specified format string
 * @param date Date to format
 * @param formatString Format string (defaults to 'MMM d, yyyy')
 * @returns Formatted date string or placeholder if invalid
 */
export const formatDate = (date: Date | string | number | Timestamp | null | undefined, formatString: string = 'MMM d, yyyy'): string => {
  const dateObj = toDate(date);
  if (!dateObj) return '—';
  
  try {
    return format(dateObj, formatString);
  } catch (e) {
    console.error("Error formatting date:", date, e);
    return '—';
  }
};

const ET_FIELD_FORMAT = new Intl.DateTimeFormat("en-US", {
  timeZone: DASHBOARD_METRICS_TIMEZONE,
  hourCycle: "h23",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

/**
 * Format a date as US Eastern wall-clock time, whatever the browser's zone.
 *
 * `formatDate` renders in the viewer's local zone, so one instant reads as two
 * different times on two machines. The admin reporting day is Eastern
 * (`DASHBOARD_METRICS_TIMEZONE`), and an ET time next to ET day buckets is the
 * only reading that agrees with them.
 *
 * Supports the field tokens `yyyy MM dd HH mm ss`; every other character is
 * literal. It reads the fields from `Intl` rather than building a shifted local
 * `Date` for date-fns, because a shifted `Date` can land in the viewer's own
 * DST gap and move an hour.
 */
export const formatDateEt = (
  date: Date | string | number | Timestamp | null | undefined,
  formatString: string = "yyyy-MM-dd HH:mm"
): string => {
  const dateObj = toDate(date);
  // `toDate` does not validate a Timestamp's `toDate()`, and `Intl` throws on an
  // Invalid Date. Throwing inside a column render takes down the whole table.
  if (!dateObj || !isValid(dateObj)) return '—';

  const fields: Record<string, string> = {};
  for (const part of ET_FIELD_FORMAT.formatToParts(dateObj)) fields[part.type] = part.value;
  const tokens: Record<string, string | undefined> = {
    yyyy: fields.year,
    MM: fields.month,
    dd: fields.day,
    HH: fields.hour,
    mm: fields.minute,
    ss: fields.second,
  };
  return formatString.replace(/yyyy|MM|dd|HH|mm|ss/g, (token) => tokens[token] ?? token);
};

/**
 * Format a date range as a human-readable string
 * @param startDate Start date
 * @param endDate End date
 * @returns Formatted date range string
 */
export const formatDateRange = (startDate: any, endDate: any): string => {
  const start = toDate(startDate);
  const end = toDate(endDate);
  
  if (!start) return '—';
  
  if (!end || start.getTime() === end.getTime()) {
    return formatDate(start, 'MMM d, yyyy');
  }
  
  // Same month and year
  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    return `${format(start, 'MMM d')}–${format(end, 'd, yyyy')}`;
  }
  
  // Same year
  if (start.getFullYear() === end.getFullYear()) {
    return `${format(start, 'MMM d')}–${format(end, 'MMM d, yyyy')}`;
  }
  
  // Different years
  return `${formatDate(start)} – ${formatDate(end)}`;
};

/**
 * Check if a date is in the future
 * @param date Date to check
 * @returns boolean
 */
export const isFutureDate = (date: any): boolean => {
  const dateObj = toDate(date);
  if (!dateObj) return false;
  
  return dateObj > new Date();
};

/**
 * Check if a date is in the past
 * @param date Date to check
 * @returns boolean
 */
export const isPastDate = (date: any): boolean => {
  const dateObj = toDate(date);
  if (!dateObj) return false;
  
  return dateObj < new Date();
};

/**
 * Format date for display in UI components
 * @param date Date to format
 * @returns Formatted date string for UI display
 */
export const formatDateForDisplay = (date: any): string => {
  return formatDate(date, 'MMM d, yyyy');
};

/**
 * Format datetime for display in UI components
 * @param date Date to format
 * @returns Formatted datetime string for UI display
 */
export const formatDateTimeForDisplay = (date: any): string => {
  return formatDate(date, 'MMM d, yyyy h:mm a');
};
